import React, { useState, useEffect, useMemo } from "react";
import { AlertTriangle, Bus } from "lucide-react";
import {
  generateFeeBatch,
  generateFeeBatchPortal,
  getAllFeeHeads,
  getAllFeeHeadsPortal,
  getFeeStructuresByYear,
  getFeeStructuresByYearPortal,
} from "../../api/fees/feeApi";
import {
  getAllAcademicYears,
  getAllAcademicYearsPortal,
} from "../../api/academicyear/academicYearApi";
import { getAllClasses } from "../../api/student/studentApi";
import { getAllClassesForPortal } from "../../api/student/studentPortalApi";
import {
  Modal,
  Button,
  Input,
  Select,
  Badge,
  Alert,
  Checkbox,
  Spinner,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const structureLabel = (s) => {
  if (s.name) return s.name;
  const head = s.feeHeadId?.name || s.feeHeadName || "";
  const amount = s.amount != null ? `₹${s.amount}` : "";
  return [head, amount].filter(Boolean).join(" · ") || "Structure";
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const MONTH_OPTIONS = [
  { label: "Select Month", value: "" },
  ...MONTHS.map((m) => ({ label: m, value: m })),
];

const isTransportHead = (h) =>
  h && (h.type === "TRANSPORT" || h.source === "TRANSPORT_MODULE");

const GenerationDebugDetails = ({ details, classNameLookup, feeHeadNameLookup }) => {
  if (!details) return null;

  const missing = details.missingFeeStructures || [];
  const duplicateSkipCount = details.duplicateSkipCount;
  const transportHeadsSkipped = details.transportHeadsSkipped || [];

  return (
    <Alert variant="danger" title="Generation Issue Breakdown">
      <div className="mt-1 space-y-2 text-xs">
        {missing.length > 0 && (
          <div>
            <div className="font-semibold text-danger-700">
              Missing fee structures ({missing.length}):
            </div>
            <ul className="mt-0.5 list-disc list-inside space-y-0.5">
              {missing.map((m, i) => (
                <li key={i}>
                  {classNameLookup?.[m.classId] || `Class ${m.classId}`} +{" "}
                  {feeHeadNameLookup?.[m.feeHeadId] ||
                    m.feeHeadName ||
                    `Fee Head ${m.feeHeadId}`}
                </li>
              ))}
            </ul>
          </div>
        )}

        {typeof duplicateSkipCount === "number" && duplicateSkipCount > 0 && (
          <div>
            <span className="font-semibold text-danger-700">Duplicate skip count:</span>{" "}
            {duplicateSkipCount}
          </div>
        )}

        {transportHeadsSkipped.length > 0 && (
          <div>
            <span className="font-semibold text-danger-700">
              Transport-module heads skipped:
            </span>{" "}
            {transportHeadsSkipped
              .map((id) => feeHeadNameLookup?.[id] || id)
              .join(", ")}
          </div>
        )}

        <details className="pt-1">
          <summary className="cursor-pointer select-none text-neutral-500 dark:text-neutral-400 hover:underline">
            Raw JSON Breakdown
          </summary>
          <pre className="mt-1 max-h-32 overflow-auto rounded bg-white/70 dark:bg-neutral-900/70 p-2 font-mono text-[10px] text-neutral-800 dark:text-neutral-100 border border-neutral-200 dark:border-neutral-800">
            {JSON.stringify(details, null, 2)}
          </pre>
        </details>
      </div>
    </Alert>
  );
};

const GenerateBatchModal = ({
  isOpen,
  onClose,
  onSuccess,
  onGenerateError,
  isPortal = false,
}) => {
  const [form, setForm] = useState({
    academicYearId: "",
    batchName: "",
    month: "",
    year: new Date().getFullYear(),
    dueDate: "",
    feeStructureIds: [],
    includeTransport: true,
  });
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [feeHeads, setFeeHeads] = useState([]);
  const [transportHeads, setTransportHeads] = useState([]);
  const [structures, setStructures] = useState([]);
  const [fetchingStructures, setFetchingStructures] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      academicYearId: "",
      batchName: "",
      month: "",
      year: new Date().getFullYear(),
      dueDate: "",
      feeStructureIds: [],
      includeTransport: true,
    });
    setErrorDetails(null);

    const load = async () => {
      setFetching(true);
      try {
        const [ayRes, classData, headsRes] = await Promise.all([
          isPortal ? getAllAcademicYearsPortal() : getAllAcademicYears(),
          isPortal ? getAllClassesForPortal() : getAllClasses(),
          isPortal
            ? getAllFeeHeadsPortal({ isActive: true })
            : getAllFeeHeads({ isActive: true }),
        ]);
        const years = ayRes?.data || ayRes || [];
        setAcademicYears(Array.isArray(years) ? years : []);
        setClasses(classData || []);
        const heads = Array.isArray(headsRes?.data || headsRes)
          ? headsRes?.data || headsRes
          : [];
        setFeeHeads(heads.filter((h) => !isTransportHead(h)));
        setTransportHeads(heads.filter((h) => isTransportHead(h)));
        const active = (Array.isArray(years) ? years : []).find((y) => y.isActive);
        if (active) setForm((p) => ({ ...p, academicYearId: active._id }));
      } catch {
        toast.error("Failed to load form data.");
      } finally {
        setFetching(false);
      }
    };
    load();
  }, [isOpen, isPortal]);

  useEffect(() => {
    if (!form.academicYearId) {
      setStructures([]);
      return;
    }
    setFetchingStructures(true);
    const fn = isPortal ? getFeeStructuresByYearPortal : getFeeStructuresByYear;
    fn({ academicYearId: form.academicYearId })
      .then((res) => {
        const list = res?.data || res || [];
        setStructures(Array.isArray(list) ? list : []);
      })
      .catch(() => setStructures([]))
      .finally(() => setFetchingStructures(false));
  }, [form.academicYearId, isPortal]);

  const classNameLookup = classes.reduce((acc, c) => {
    acc[c._id] = `Class ${c.className}`;
    return acc;
  }, {});

  const feeHeadNameLookup = [...feeHeads, ...transportHeads].reduce((acc, h) => {
    acc[h._id] = `${h.name} (${h.code})`;
    return acc;
  }, {});

  const classNameById = useMemo(() => {
    const map = {};
    classes.forEach((c) => {
      map[c._id] = c.className || c.name || `Class ${c._id}`;
    });
    return map;
  }, [classes]);

  const academicYearOptions = useMemo(
    () => [
      { label: "Select Academic Year", value: "" },
      ...academicYears.map((y) => ({
        label: `${y.yearName}${y.isActive ? " (Active)" : ""}`,
        value: y._id,
      })),
    ],
    [academicYears]
  );

  const structureRows = useMemo(() => {
    const byClass = {};
    structures.forEach((s) => {
      const head = s.feeHeadId || {};
      if (isTransportHead(head)) return;
      const clsId = s.classId?._id || s.classId;
      const clsLabel =
        s.classId?.className ||
        s.classId?.name ||
        classNameById[clsId] ||
        `Class ${clsId}`;
      byClass[clsId] = byClass[clsId] || {
        classId: clsId,
        label: clsLabel,
        items: [],
      };
      byClass[clsId].items.push(s);
    });
    return Object.values(byClass).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true })
    );
  }, [structures, classNameById]);

  const toggleStructure = (id) => {
    setForm((prev) => {
      const set = new Set(prev.feeStructureIds.map(String));
      const s = String(id);
      if (set.has(s)) set.delete(s);
      else set.add(s);
      return { ...prev, feeStructureIds: Array.from(set) };
    });
  };

  const toggleClassRow = (groupIds, allChecked) => {
    setForm((prev) => {
      const set = new Set(prev.feeStructureIds.map(String));
      if (allChecked) groupIds.forEach((id) => set.delete(id));
      else groupIds.forEach((id) => set.add(id));
      return { ...prev, feeStructureIds: Array.from(set) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.academicYearId ||
      !form.batchName.trim() ||
      !form.month ||
      !form.year ||
      !form.dueDate
    ) {
      toast.error("Academic year, batch name, month, year and due date are required.");
      setErrorDetails(null);
      return;
    }
    if (!form.feeStructureIds.length && !form.includeTransport) {
      toast.error(
        "Select at least one fee structure, or enable Include transport fees."
      );
      setErrorDetails(null);
      return;
    }
    if (form.includeTransport && transportHeads.length === 0) {
      toast.error(
        "No active Transport fee head found. Create a fee head with type TRANSPORT first."
      );
      setErrorDetails(null);
      return;
    }

    setLoading(true);
    setErrorDetails(null);
    try {
      const payload = {
        academicYearId: form.academicYearId,
        batchName: form.batchName.trim(),
        month: form.month,
        year: Number(form.year),
        dueDate: form.dueDate,
        feeStructureIds: form.feeStructureIds,
        includeTransport: Boolean(form.includeTransport),
      };
      const response = isPortal
        ? await generateFeeBatchPortal(payload)
        : await generateFeeBatch(payload);

      onSuccess?.(response);
      onClose();
    } catch (err) {
      const body = err.response?.data;
      toast.error(body?.message || "Failed to generate batch.");
      setErrorDetails(body?.details || null);
      onGenerateError?.(body);
    } finally {
      setLoading(false);
    }
  };

  const transportHeadLabel =
    transportHeads.length === 0
      ? "No transport fee head configured"
      : transportHeads.map((h) => h.name).join(", ");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate Fee Batch"
      className="w-full max-w-[calc(100vw-1rem)] sm:max-w-lg md:max-w-xl mx-2 sm:mx-auto my-2 sm:my-auto max-h-[95vh] sm:max-h-[85vh] flex flex-col overflow-hidden"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2 w-full">
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="generate-batch-form"
            variant="primary"
            isLoading={loading}
            disabled={fetching}
          >
            Generate Batch
          </Button>
        </div>
      }
    >
      <form
        id="generate-batch-form"
        onSubmit={handleSubmit}
        className="space-y-4 pt-1"
      >
        

        <GenerationDebugDetails
          details={errorDetails}
          classNameLookup={classNameLookup}
          feeHeadNameLookup={feeHeadNameLookup}
        />

        {fetching ? (
          <div className="flex items-center justify-center py-6 gap-2 text-sm text-neutral-500 dark:text-neutral-400">
            <Spinner size="sm" />
            <span>Loading form configuration...</span>
          </div>
        ) : (
          <>
            <Select
              label="Academic Year *"
              options={academicYearOptions}
              value={form.academicYearId}
              onChange={(val) => setForm((p) => ({ ...p, academicYearId: val }))}
              required
            />

            <Input
              label="Batch Name *"
              type="text"
              value={form.batchName}
              onChange={(e) =>
                setForm((p) => ({ ...p, batchName: e.target.value }))
              }
              placeholder="e.g. April 2026 Tuition"
              required
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label="Month *"
                options={MONTH_OPTIONS}
                value={form.month}
                onChange={(val) => setForm((p) => ({ ...p, month: val }))}
                required
              />

              <Input
                label="Year *"
                type="number"
                value={form.year}
                onChange={(e) =>
                  setForm((p) => ({ ...p, year: e.target.value }))
                }
                required
              />
            </div>

            <Input
              label="Due Date *"
              type="date"
              value={form.dueDate}
              onChange={(e) =>
                setForm((p) => ({ ...p, dueDate: e.target.value }))
              }
              required
            />

            {/* ── Structure-based heads ── */}
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center justify-between gap-1.5">
                <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wide">
                  Classes & Fee Heads
                </label>
                <Badge variant="primary">
                  {form.feeStructureIds.length} fee head
                  {form.feeStructureIds.length === 1 ? "" : "s"} selected
                </Badge>
              </div>

              {!form.academicYearId && (
                <div className="rounded-card border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 p-4 text-center text-xs text-neutral-500 dark:text-neutral-400">
                  Select an academic year above to view available fee structures.
                </div>
              )}

              {form.academicYearId && fetchingStructures && (
                <div className="flex items-center justify-center gap-2 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 text-xs text-neutral-500 dark:text-neutral-400">
                  <Spinner size="sm" />
                  <span>Loading fee structures...</span>
                </div>
              )}

              {form.academicYearId &&
                !fetchingStructures &&
                structureRows.length === 0 && (
                  <div className="rounded-card border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 p-4 text-center text-xs text-neutral-500 dark:text-neutral-400">
                    No fee structures found for this academic year.
                  </div>
                )}

              {structureRows.length > 0 && (
                <div className="rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 divide-y divide-neutral-100 dark:divide-neutral-800">
                  {structureRows.map((row) => {
                    const groupIds = row.items.map((s) => String(s._id));
                    const selectedCount = groupIds.filter((id) =>
                      form.feeStructureIds.map(String).includes(id)
                    ).length;
                    const allChecked = selectedCount === groupIds.length;
                    const someChecked = selectedCount > 0 && !allChecked;
                    const rowFlagged = (
                      errorDetails?.missingFeeStructures || []
                    ).some((m) => m.classId === row.classId);

                    return (
                      <div
                        key={row.classId}
                        className="p-3 flex flex-col gap-2.5 sm:flex-row sm:items-start sm:justify-between"
                      >
                        <div className="w-full sm:w-36 sm:shrink-0">
                          <Checkbox
                            label={
                              <span
                                className={`font-medium break-words ${
                                  rowFlagged
                                    ? "text-danger-500 flex flex-wrap items-center gap-1"
                                    : "text-neutral-900 dark:text-neutral-100"
                                }`}
                              >
                                {row.label}
                                {rowFlagged && (
                                  <AlertTriangle className="h-3.5 w-3.5 text-danger-500 shrink-0" />
                                )}
                              </span>
                            }
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={() => toggleClassRow(groupIds, allChecked)}
                          />
                          <p className="pl-6 text-[11px] text-neutral-400 dark:text-neutral-500">
                            {selectedCount}/{groupIds.length} selected
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0 justify-start">
                          {row.items.map((s) => {
                            const checked = form.feeStructureIds
                              .map(String)
                              .includes(String(s._id));
                            return (
                              <button
                                type="button"
                                key={s._id}
                                onClick={() => toggleStructure(s._id)}
                                className={`inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 font-medium transition-colors border focus:outline-none focus:ring-2 focus:ring-primary-400 max-w-full ${ checked ? "bg-primary-900 text-white border-primary-900" : "bg-neutral-50 dark:bg-neutral-950 text-neutral-700 dark:text-neutral-200 border-neutral-200 dark:border-neutral-800 hover:border-primary-400" }`}
                              >
                                <span className="break-words">{structureLabel(s)}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Transport (module-driven amount) ── */}
            <div
              className={`rounded-card border p-3 space-y-2 ${ form.includeTransport ? "border-primary-200 bg-primary-50/60" : "border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950" }`}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <Checkbox
                    checked={form.includeTransport}
                    onChange={(checked) =>
                      setForm((p) => ({
                        ...p,
                        includeTransport:
                          typeof checked === "boolean"
                            ? checked
                            : !p.includeTransport,
                      }))
                    }
                    disabled={transportHeads.length === 0}
                    label={
                      <span className="font-medium text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-1.5">
                        <Bus className="h-4 w-4 text-primary-700 dark:text-primary-400" />
                        Include transport fees
                      </span>
                    }
                  />
                </div>
              </div>

              <p className="text-xs text-neutral-600 dark:text-neutral-300 pl-6">
                {transportHeads.length === 0 ? (
                  <>
                    Create an active fee head with type <strong>TRANSPORT</strong>{" "}
                    first. Amount is never entered here — it comes from each
                    student&apos;s active transport assignment.
                  </>
                ) : (
                  <>
                    Fee head: <strong>{transportHeadLabel}</strong>
                    <br />
                    Amount is taken automatically from each student&apos;s{" "}
                    <strong>active transport assignment</strong> (pincode /
                    vehicle charge). No structure amount is used.
                  </>
                )}
              </p>

              {form.includeTransport && transportHeads.length > 0 && (
                <div className="pl-6 flex flex-wrap gap-1.5">
                  {transportHeads.map((h) => (
                    <Badge key={h._id} variant="primary">
                      {h.name}
                      {h.code ? ` (${h.code})` : ""}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};

export default GenerateBatchModal;
